import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { handleExternalNav } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Calendar, Music, Instagram, Globe, ExternalLink } from "lucide-react";
import { apiClient } from "@/App";
import { format } from "date-fns";
import { gigLocalDate } from "@/utils/dateUtils";
import { storeReferral } from "@/lib/referral";

// Make a user-entered URL safe to use as an <a href>. A bare "example.com"
// would otherwise be treated as a path relative to the profile page and break;
// prepend https:// when there's no scheme. Returns null for empty input.
const toAbsoluteUrl = (url) => {
  const u = (url || "").trim();
  if (!u) return null;
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
};

const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function MusicianProfile() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Capture this musician as the referrer, so a signup from their profile
    // (via the CTA or the QR they shared) is attributed back to them.
    storeReferral(username);
    const fetchProfile = async () => {
      try {
        const res = await apiClient.get(`/musician/${username}`);
        setProfile(res.data);
        document.title = `${res.data.name || username} — Fretless`;
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
    return () => { document.title = "Fretless"; };
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Music className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Musician not found</h2>
            <p className="text-muted-foreground text-sm">This profile doesn't exist.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="musician-profile">
      <div className="h-1 bg-primary w-full" />
      <div className="max-w-lg mx-auto px-4 py-8 space-y-4">
        {/* Header */}
        <div className="text-center space-y-3">
          {profile.picture ? (
            <img src={profile.picture} alt={profile.name} className="w-20 h-20 rounded-full mx-auto border-2 border-primary/30" />
          ) : (
            <div className="w-20 h-20 rounded-full mx-auto bg-primary/10 flex items-center justify-center">
              <Music className="h-10 w-10 text-primary" />
            </div>
          )}
          <div>
            <h1 className="font-heading text-2xl font-bold" data-testid="profile-name">{profile.name}</h1>
            {profile.artist_names && profile.artist_names !== profile.name && (
              <p className="text-muted-foreground text-sm mt-1">{profile.artist_names}</p>
            )}
          </div>
          {/* Social links */}
          <div className="flex justify-center gap-3">
            {profile.instagram_handle && (
              <a href={`https://instagram.com/${profile.instagram_handle}`} target="_blank" rel="noopener noreferrer" onClick={handleExternalNav} className="text-muted-foreground hover:text-pink-500 transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
            )}
            {profile.twitter_handle && (
              <a href={`https://x.com/${profile.twitter_handle}`} target="_blank" rel="noopener noreferrer" onClick={handleExternalNav} className="text-muted-foreground hover:text-white transition-colors">
                <XLogo className="h-4 w-4" />
              </a>
            )}
            {toAbsoluteUrl(profile.website) && (
              <a href={toAbsoluteUrl(profile.website)} target="_blank" rel="noopener noreferrer" onClick={handleExternalNav} className="text-muted-foreground hover:text-primary transition-colors" aria-label="Website">
                <Globe className="h-5 w-5" />
              </a>
            )}
          </div>
        </div>

        {/* Upcoming Gigs */}
        {profile.upcoming_gigs && profile.upcoming_gigs.length > 0 && (
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Upcoming Shows</h3>
              <div className="space-y-3">
                {profile.upcoming_gigs.map((gig, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Calendar className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{gig.artist_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {gig.venue_name}{gig.city ? `, ${gig.city}` : ""} — {gig.date ? format(new Date((gigLocalDate(gig.date) || '').replace(/-/g, '/')), "d MMM yyyy") : ""}
                        {gig.start_time ? ` at ${gig.start_time}` : ""}
                      </p>
                    </div>
                    {toAbsoluteUrl(gig.ticket_link) && (
                      <a
                        href={toAbsoluteUrl(gig.ticket_link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleExternalNav}
                        className="shrink-0 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 transition-opacity"
                        data-testid={`ticket-link-${i}`}
                      >
                        Tickets{gig.ticket_price ? ` · ${gig.ticket_price}` : ""}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Signup CTA — convert visiting musicians into Fretless users. */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-5 text-center space-y-3">
            <Music className="h-7 w-7 text-primary mx-auto" />
            <div>
              <h3 className="font-heading text-base font-semibold">Are you a musician?</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Fretless keeps your gigs, invoices, travel and socials in one place — and gives you a shareable profile like this one.
              </p>
            </div>
            <Button asChild className="w-full sm:w-auto" data-testid="profile-signup-cta">
              <a href={`/?ref=${encodeURIComponent(username)}`}>Get started free</a>
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground pt-2">
          <a href="/" className="text-primary hover:underline">Fretless</a> — The Musician's Command Center
        </p>
      </div>
    </div>
  );
}
